import { useContext, useState } from 'react';
import { fetchWithTokenRefresh } from '../../../../../../../../api/fetchWithTokenRefresh';
import endpoints from '../../../../../../../../api/endpoints';
import { useDialog } from '../../../../../../../common/dialog/useDialog';
import { ToastNotificationContext } from '../../../../../../../common/toast-notification';
import { DialogError } from '../../../../../../../common/business-error';
import { handleResponseError } from '../../../../../../../../api/handleResponseError';
import { resendUserInvitationBusinessError } from '../businessErrors';
import { ToastProps } from '../../../../../../../common/toast-notification/interfaces';

type ApiResponse = {
  message: string;
  status: string;
};

interface Props {
  handleSuccess: () => void;
}

export const useResendUserInvitation = ({ handleSuccess }: Props) => {
  const { openToast } = useContext(ToastNotificationContext);
  const { isDialogOpen, handleOpenDialog, handleCloseDialog } = useDialog();
  const [resendLoading, setLoading] = useState(false);
  const [resendError, setError] = useState<DialogError>(null);

  const handleApiResponse = (response: ApiResponse) => {
    switch (response.status) {
      case 'OK': {
        openToast({
          message: 'Invitation was resend!',
          severity: 'success'
        });
        break;
      }
      case 'FAILED': {
        const toastResponse: ToastProps = {
          message: 'Failed to resend invitation',
          severity: 'error'
        };
        openToast(toastResponse);
        setError({ message: toastResponse.message, severity: 'error' });
        break;
      }
      default: {
        const toastResponse: ToastProps = {
          message: 'unknown error',
          severity: 'error'
        };
        openToast(toastResponse);
        setError({ message: toastResponse.message, severity: 'error' });
        break;
      }
    }
  };

  const handleResendUserInvitation = async (id: string) => {
    try {
      setLoading(true);
      const response = await fetchWithTokenRefresh(endpoints.userInvitations(id), {
        method: 'post'
      });
      const jsonResponse = await response.json();
      if (response.status !== 200) {
        await handleResponseError(response);
      }

      const parsed: ApiResponse = jsonResponse;
      handleApiResponse(parsed);

      setLoading(false);
      handleCloseDialog();
      handleSuccess();
    } catch (e) {
      const { message, severity } = resendUserInvitationBusinessError();

      setError({ message, severity });
      setLoading(false);
    }
  };

  const closeDialog = () => {
    setError(null);
    handleCloseDialog();
  };

  return {
    userResendInvitationModalOpen: isDialogOpen,
    handleOpenResendUserInvitationModal: handleOpenDialog,
    handleCloseResendUserInvitationModal: closeDialog,
    handleResendUserInvitation,
    resendLoading,
    resendError
  };
};
